import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createCoachMatchProposal,
  getCoachMatchPlay,
  getFamilyMatchPlayInterests,
  getFamilyMatchPlayProposals,
  respondToFamilyMatchProposal,
  reportFamilyMatchProposal,
  saveFamilyMatchPlayInterest,
  scheduleCoachMatchProposal,
} from '../lib/tenantMatchPlay';

export const tenantMatchPlayKeys = {
  all: ['tenant-match-play'] as const,
  coach: () => [...tenantMatchPlayKeys.all, 'coach'] as const,
  familyInterests: () => [...tenantMatchPlayKeys.all, 'family-interests'] as const,
  familyProposals: () => [...tenantMatchPlayKeys.all, 'family-proposals'] as const,
};

export function useCoachMatchPlay(enabled = true) {
  const client = useQueryClient();
  const dashboard = useQuery({ queryKey: tenantMatchPlayKeys.coach(), queryFn: getCoachMatchPlay, enabled, retry: false });
  const propose = useMutation({
    mutationFn: ({ athleteOneId, athleteTwoId }: { athleteOneId: number; athleteTwoId: number }) =>
      createCoachMatchProposal(athleteOneId, athleteTwoId),
    onSuccess: () => void client.invalidateQueries({ queryKey: tenantMatchPlayKeys.coach() }),
  });
  const schedule = useMutation({
    mutationFn: ({ proposalId, ...payload }: { proposalId: number; programId: number; startsAt: string; endsAt: string; locationLabel?: string }) =>
      scheduleCoachMatchProposal(proposalId, payload),
    onSuccess: () => void client.invalidateQueries({ queryKey: tenantMatchPlayKeys.coach() }),
  });
  return { dashboard, propose, schedule };
}

export function useFamilyMatchPlay(enabled = true) {
  const client = useQueryClient();
  const interests = useQuery({ queryKey: tenantMatchPlayKeys.familyInterests(), queryFn: getFamilyMatchPlayInterests, enabled, retry: false });
  const proposals = useQuery({ queryKey: tenantMatchPlayKeys.familyProposals(), queryFn: getFamilyMatchPlayProposals, enabled, retry: false });
  const refresh = () => {
    void client.invalidateQueries({ queryKey: tenantMatchPlayKeys.familyInterests() });
    void client.invalidateQueries({ queryKey: tenantMatchPlayKeys.familyProposals() });
  };
  const saveInterest = useMutation({ mutationFn: saveFamilyMatchPlayInterest, onSuccess: refresh });
  const respond = useMutation({
    mutationFn: ({ proposalId, decision }: { proposalId: number; decision: 'APPROVED' | 'DECLINED' }) =>
      respondToFamilyMatchProposal(proposalId, decision),
    onSuccess: refresh,
  });
  const report = useMutation({
    mutationFn: ({ proposalId, reasonCode }: { proposalId: number; reasonCode: 'INAPPROPRIATE' | 'PRIVACY' | 'SAFETY' | 'OTHER' }) =>
      reportFamilyMatchProposal(proposalId, reasonCode),
  });
  return { interests, proposals, saveInterest, respond, report };
}
